import { UserAvatar } from '@/components/ui/user-avatar';
import { useI18n } from '@/hooks/useI18n';
import { IPaymentsPerson } from '@/lib/payments/models/payments-dashboard.model';

/**
 * Una persona dentro una riga: pallino, nome, e -- dove serve -- l'email sotto.
 *
 * Da `md` in giu' resta il solo pallino. Non e' una scelta di gusto: la tabella ha sei
 * colonne, e su uno schermo stretto i nomi per esteso spingono fuori gli importi e le
 * scadenze, che sono il motivo per cui si apre questa lista. Li' il nome si legge
 * aprendo la scheda laterale, che e' a un clic.
 *
 * Diventa un bottone solo dove c'e' davvero una scheda da aprire: una manina su qualcosa
 * che non reagisce promette piu' di quanto mantiene.
 */
interface PersonCellProps {
    person: IPaymentsPerson | null;
    /** Chi e' questa persona rispetto alla riga: finisce nell'etichetta per gli screen
     *  reader e fa da ripiego quando non c'e' ne' nome ne' email. */
    hint: string;
    onOpen?: () => void;
    /** L'email sotto il nome: serve al cliente, dove distingue due omonimi. */
    withEmail?: boolean;
}

export const PersonCell = ({ person, hint, onOpen, withEmail = false }: PersonCellProps) => {
    const { t } = useI18n();

    if (!person) return <span className="text-gray-400">—</span>;

    const displayName = person.name ?? person.email ?? hint;
    const isOpenable = Boolean(onOpen);

    const content = (
        <>
            {/* Nessun tooltip sul pallino: il nome e' gia' scritto accanto, e un riquadro
                che ripete cio' che si legge si apre addosso alla riga ogni volta che il
                mouse passa per andare altrove. Il dettaglio sta nella scheda laterale.

                Il cerchio si accende al passaggio anche quando il nome e' nascosto: sotto
                `md` e' l'unica parte visibile, e senza quel segnale la cella sembrerebbe
                inerte proprio dove e' piu' difficile capirlo. */}
            <UserAvatar
                fullName={person.name}
                email={person.email}
                imageUrl={person.avatar_url}
                size="sm"
                className={
                    isOpenable
                        ? // cursor-pointer esplicito: il trigger del tooltip dentro
                          // UserAvatar porta `cursor-default`, che sull'area del pallino
                          // vincerebbe sul puntatore ereditato dal bottone.
                          'cursor-pointer transition-shadow group-hover:ring-2 group-hover:ring-gray-300 group-hover:ring-offset-1'
                        : undefined
                }
            />
            <span className="hidden md:flex flex-col leading-tight min-w-0 text-left">
                <span
                    className={`truncate text-gray-700 ${
                        isOpenable
                            ? 'group-hover:text-gray-900 group-hover:underline underline-offset-2'
                            : ''
                    }`}
                >
                    {displayName}
                </span>
                {withEmail && person.email && (
                    <span className="truncate text-xs text-gray-500">{person.email}</span>
                )}
            </span>
        </>
    );

    if (!onOpen) {
        return <span className="flex items-center gap-2 min-w-0">{content}</span>;
    }

    return (
        <button
            type="button"
            onClick={onOpen}
            /* Niente `title`: il pallino ha gia' il suo tooltip con nome, email e ruolo,
               e due riquadri sovrapposti sullo stesso elemento si coprono a vicenda.
               L'invito ad aprire lo da' il cambio di stile al passaggio. */
            aria-label={t('payments.actions.openDetail', { name: displayName })}
            className="group flex items-center gap-2 min-w-0 rounded-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1"
        >
            {content}
        </button>
    );
};
